import React, { useState, useEffect } from 'react';
import { getCallbacks, resolveCallback, setCallbackStatus } from '../../services/api';
import './BusinessContext.scss';
import { toast } from 'react-toastify';

const POLL_INTERVAL = 60_000; // 1 minute

const BusinessCallbacks = ({ clientId, colorData }) => {
  const [callbacks, setCallbacks] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('open');
  const [priorityFilter, setPriorityFilter] = useState('all');

  // Editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Fetch callbacks
   * - Only updates state if NEW entries exist
   * - Prevents unnecessary re-renders
   */
  const loadData = async () => {
    try {
      const data = await getCallbacks(clientId);

      setCallbacks(prev => {
        if (!prev.length) return data; // first load

        const existingIds = new Set(prev.map(c => c.id));
        const newItems = data.filter(c => !existingIds.has(c.id));

        if (newItems.length === 0) return prev;

        toast.info(`${newItems.length} new callback(s) received`);
        return [...newItems, ...prev];
      });

    } catch (err) {
      console.error('Failed to load callbacks', err);
    } finally {
      setInitialLoad(false);
    }
  };

  /**
   * Initial load + polling
   */
  useEffect(() => {
    loadData();

    const interval = setInterval(loadData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [clientId]);

  /**
   * Filters (derived state, no re-renders)
   */
  const filteredCallbacks = callbacks.filter(item => {
    const statusMatch = statusFilter === 'all' || item.status === statusFilter;
    const priorityMatch = priorityFilter === 'all' || item.priority === priorityFilter;
    return statusMatch && priorityMatch;
  });

  /**
   * Actions
   */
  const handleResolve = async () => {
    if (!editingItem?.resolution_notes) {
      toast.error('Please provide resolution notes.');
      return;
    }

    setSubmitting(true);
    try {
      await resolveCallback({
        id: editingItem.id,
        resolution_notes: editingItem.resolution_notes
      });

      toast.success('Callback resolved');

      // 🔥 Update local state directly
      setCallbacks(prev =>
        prev.map(c =>
          c.id === editingItem.id
            ? {
              ...c,
              status: 'resolved',
              resolution_notes: editingItem.resolution_notes
            }
            : c
        )
      );

      setIsEditing(false);
    } catch (err) {
      toast.error(err.message || 'Failed to resolve callback');
    } finally {
      setSubmitting(false);
    }
  };


  const handleUpdateStatus = async (id, status, priority) => {
    try {
      await setCallbackStatus({ id, status, priority });
      await loadData();
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Helpers
   */
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      default: return '#3b82f6';
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  /**
   * Initial loading only (no flashing)
   */
  if (initialLoad) {
    return <div className="text-center p-5">Loading callbacks...</div>;
  }

  return (
    <div
      className="business-context-container mt-5 mb-5"
      style={{
        borderBottom: `15px solid rgb(157 165 244)`,
        background: "rgb(216 220 248)",
        paddingBottom: '100px',
        paddingTop: '50px'
      }}
    >
 <h2 className="mb-2 poppin text-center mb-5">
<span className='text-white  p-2' style={{ background: "#9da5f3" }}><i className="fas fa-phone-alt  me-2 text-warning"></i><span>Customer Callback Requests</span></span></h2>

      {/* Filters */}
      <div className="container mb-5">
        <div className="row justify-content-center">
          <div className="col-md-8 d-flex gap-3 justify-content-center bg-white p-3 rounded shadow-sm border">
            <div style={{ maxWidth: 200 }}>
              <label className=" fw-bold mb-1">Status</label>
              <select

                className="form-select form-select-sm"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div style={{ maxWidth: 200 }}>
              <label className=" fw-bold mb-1">Severity</label>
              <select
                className="form-select form-select-sm"
                value={priorityFilter}
                onChange={e => setPriorityFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className="d-flex align-items-end">
              <button
                className="btn-glass-primary"
                onClick={() => {
                  setStatusFilter('all');
                  setPriorityFilter('all');
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="row px-4">
        {filteredCallbacks.length === 0 ? (
          <div className="col-12 text-center">
            <div className="alert alert-light border">
              No callbacks found
            </div>
          </div>
        ) : (
          filteredCallbacks.map(item => (
            <div
              key={item.id}
              className="col-md-6 col-lg-4 mb-4 callback-card"
            >
              <div className={`card h-100 shadow-sm border-0 ${item.status === 'resolved' ? 'opacity-75 bg-light' : ''}`}>
                <div className="card-header card-animate card-animate-delay-1 d-flex justify-content-between p-3">

                  <select

                    className="form-select form-select-sm text-white fw-bold border-0"
                    style={{ backgroundColor: getPriorityColor(item.priority), maxWidth: "160px" }}
                    value={item.priority}
                    disabled={item.status === 'resolved'}
                    onChange={e =>
                      handleUpdateStatus(item.id, item.status, e.target.value)
                    }
                  >
                    <option value="high">HIGH</option>
                    <option value="medium">MEDIUM</option>
                    <option value="low">LOW</option>
                  </select>

                  <span className={`fw-bold text-uppercase text-white  p-2 ${item.status === 'open' ? 'bg-danger' : 'bg-success'}`}>
                    {item.status}
                  </span>
                </div>

                <div className="card-body card-animate card-animate-delay-2">
                  <h5 className="fw-bold poppin">{item.customer_name}</h5>
                  <p className=" poppin">{item.customer_phone}</p>
                  <p className="text-black poppin">"{item.callback_text}"</p>

                  {item.resolution_notes && (
                    <div className="mt-2 p-2 bg-white border rounded">
                      <strong>Resolution:</strong>
                      <p>{item.resolution_notes}</p>
                    </div>
                  )}
                </div>

               <div className="card-footer card-animate card-animate-delay-3 d-flex justify-content-between">
                  <div className="text-black poppin fw-bold">{formatDate(item.created_at)}</div>
                  {item.status !== 'resolved' && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditingItem(item);
                        setIsEditing(true);
                      }}
                    >
                      Resolve
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Resolve Modal */}
      {isEditing && editingItem && (
        <div className="modal-overlay">
          <div className="modal-content bg-white p-4 rounded shadow">
            <h5 className="fw-bold mb-3 poppin">Resolve Callback</h5>
            <textarea
              className="form-control mb-3"
              rows="4"
              value={editingItem.resolution_notes || ''}
              onChange={e =>
                setEditingItem({ ...editingItem, resolution_notes: e.target.value })
              }
            />
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                style={{ backgroundColor: colorData?.secondary }}
                onClick={handleResolve}
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Complete & Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCallbacks;
